import { watch, onMounted, onUnmounted } from 'vue'
import { useScreenSafeArea } from '@vueuse/core'
import { measureViewportBottomGap, readViewportMetrics } from './viewportBottomGap'

// Staggered cold-start retry ladder: iOS PWAs can launch before the OS has
// reported inset values, so we re-read a few times as they settle.
const COLD_START_RETRY_DELAYS_MS = [50, 100, 250, 500, 1000]

const normalize = (value: string): string => value.trim()

// A read is "meaningful" only when it reports a non-zero pixel inset. During
// iOS PWA cold start, scroll, animation, or viewport transitions, both
// useScreenSafeArea and the :root `env(safe-area-inset-*)` fallback can report
// the same transient 0px native value. Falling back to env() in that moment
// does not protect us; it erases a known-good inline inset. So we never regress:
// write meaningful values inline, and leave non-meaningful reads untouched.
const isMeaningful = (value: string): boolean => {
  const v = normalize(value)
  return v !== '' && v !== '0' && v !== '0px'
}

const applySafeAreaInsets = (
  top: string,
  right: string,
  bottom: string,
  left: string,
): void => {
  const entries: Array<[string, string]> = [
    ['--safe-area-inset-top', top],
    ['--safe-area-inset-right', right],
    ['--safe-area-inset-bottom', bottom],
    ['--safe-area-inset-left', left],
  ]
  for (const [prop, raw] of entries) {
    if (isMeaningful(raw)) {
      document.documentElement.style.setProperty(prop, normalize(raw))
    }
  }
}

const applyViewportBottomGap = (): void => {
  const gap = measureViewportBottomGap(readViewportMetrics())
  document.documentElement.style.setProperty('--viewport-bottom-gap', `${gap}px`)
}

// Manages safe-area CSS custom properties using VueUse's useScreenSafeArea.
export function useSafeArea(): void {
  const { top, right, bottom, left, update } = useScreenSafeArea()

  const apply = () => {
    applySafeAreaInsets(top.value, right.value, bottom.value, left.value)
  }

  const refresh = () => {
    update()
    applyViewportBottomGap()
  }

  const timeoutIds: number[] = []
  let rafId: number | null = null

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') refresh()
  }

  watch([top, right, bottom, left], apply, { immediate: true })

  onMounted(() => {
    refresh()
    rafId = requestAnimationFrame(refresh)
    for (const delay of COLD_START_RETRY_DELAYS_MS) {
      timeoutIds.push(window.setTimeout(refresh, delay))
    }
    window.addEventListener('resize', refresh)
    window.addEventListener('orientationchange', refresh)
    window.addEventListener('pageshow', refresh)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.visualViewport?.addEventListener('resize', refresh)
  })

  onUnmounted(() => {
    if (rafId !== null) cancelAnimationFrame(rafId)
    for (const id of timeoutIds) clearTimeout(id)
    window.removeEventListener('resize', refresh)
    window.removeEventListener('orientationchange', refresh)
    window.removeEventListener('pageshow', refresh)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.visualViewport?.removeEventListener('resize', refresh)
  })
}
